import React from 'react';

import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import 'symbol-observable';

import App from './App';

if (env.Sentry.dsn !== null) {
    Sentry.init({
        dsn: env.Sentry.dsn,
        integrations: [new Sentry.BrowserTracing()],
        tracesSampleRate: 0,
        environment: env.NODE_ENV,

        // Stratégie de filtrage :
        // 1. allowUrls → n'accepte que les erreurs venant de notre code
        // 2. denyUrls → exclut les extensions navigateur (erreurs sans stacktrace)
        // 3. ignoreErrors → bruit navigateur résiduel qui se produit DANS notre code
        // 4. beforeSend → filtrage contextuel pour les cas complexes

        // 1. Whitelist : seules les erreurs dont la stacktrace pointe vers nos domaines
        //    sont envoyées. Les erreurs sans stacktrace passent aussi (sécurité).
        //    Remplace ~15 patterns de l'ancien ignoreErrors (scripts tiers, extensions, etc.)
        allowUrls: [/yurplan\.com/i, /yurplan\.me/i, /yp\.events/i, /ypl\.me/i],

        // 2. Exclut les erreurs provenant d'extensions navigateur
        denyUrls: [
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            /^moz-extension:\/\//i,
            /^safari-extension:\/\//i,
            /^about:blank$/,
            // Chrome Mobile iOS (WKWebView) — stacktrace opaque `undefined:10:73`, pas de code first-party
            /^undefined$/,
        ],

        // 3. Bruit navigateur/plateforme connu qui se produit dans notre code
        ignoreErrors: [
            'Non-Error promise rejection captured', // https://github.com/getsentry/sentry-javascript/issues/3440
            'ResizeObserver loop completed with undelivered notifications', // Limitation navigateur, pas un bug
        ],

        // 4. Filtrage contextuel sur le type d'erreur et le contexte
        beforeSend(event, hint) {
            const error = hint?.originalException;

            // Google Translate modifie le DOM → React ne peut plus réconcilier (React #11538)
            // Ces erreurs ont une stacktrace dans notre code mais ne sont pas de vrais bugs
            if (
                error instanceof DOMException &&
                /removeChild|insertBefore|appendChild/.test(error.message)
            ) {
                return null;
            }

            // Safari iOS : fetch interrompu quand l'utilisateur quitte la page
            if (error instanceof TypeError && error.message === 'Load failed') {
                return null;
            }

            return event;
        },
    });
}

const container = document.querySelector('#app');
if (container === null) {
    throw new Error('No container found');
}
const appName = container.getAttribute('data-app-name');
if (appName === null) {
    throw new Error('No application found');
}
const root = createRoot(container);
root.render(<App appName={appName as Configuration.AppName} />);
